import { Injectable } from '@angular/core';
import { getFirestore, collection, doc, onSnapshot, setDoc } from 'firebase/firestore';
import { ContainerId, getRelayNumber } from '../models/drawer';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Container } from '../models/container.model';
import { ProviderOrder } from '../../calvin-emr-shared/models/provider-order.model';
import { COLLECTION_KNIGHTMEDS_CONTAINERS, COLLECTION_KNIGHTMEDS_INSTANCES } from 'src/app/global';
import { resolveKnightmedsInstanceId } from 'src/environments/runtime-config';

const VENDOR_ID = 0x5131;
const PRODUCT_ID = 0x2007;

/** How long to pulse a relay 'on' before switching it back 'off' to open a container, in milliseconds */
const RELAY_PULSE_MS = 100;

/** The physical color of a cabinet, from its knightmedsInstances/{id} document's `color` field */
export type CabinetColor = 'maroon' | 'black';

/**
 * A service to get information about and control the containers of the Knightmeds
 */
@Injectable({ providedIn: 'root' })
export class ContainersService {
  private device: HIDDevice | null = null;
  private openContainerIds = new Set<ContainerId>();
  private anyContainersOpenListeners: ((anyOpen: boolean) => void)[] = [];

  get isDeviceConnected(): boolean {
    return this.device?.opened ?? false;
  }

  /** Connects to a previously-authorized relay device without a user gesture. Returns true if connected. */
  async tryAutoConnect(): Promise<boolean> {
    const devices = await navigator.hid.getDevices();
    const device = devices.find(d => d.vendorId === VENDOR_ID && d.productId === PRODUCT_ID);
    if (!device) {
      return false;
    }
    this.device = device;
    // The service is a singleton, so this.device can already be open from an
    // earlier call (e.g. re-entering the patients page after having
    // connected before) -- open() throws InvalidStateError if called again
    // on an already-open device.
    if (!device.opened) {
      await device.open();
    }
    return true;
  }

  /** Opens a browser dialog to select and connect to the relay HID device. Requires a user gesture. */
  async connectDevice(): Promise<void> {
    const devices = await navigator.hid.requestDevice({
      filters: [{ vendorId: VENDOR_ID, productId: PRODUCT_ID }],
    });
    if (devices.length === 0) {
      return;
    }
    this.device = devices[0];
    if (!this.device.opened) {
      await this.device.open();
    }
  }

  /**
   * Live color of this physical cabinet, read from the `color` field on the
   * knightmedsInstances/{instanceId} document itself (not a container doc).
   * Defaults to 'maroon' -- the original look -- if the field is unset or
   * holds anything other than 'black'.
   */
  getCabinetColor(): Observable<CabinetColor> {
    const db = getFirestore();
    return new Observable<CabinetColor>(observer =>
      onSnapshot(
        doc(db, COLLECTION_KNIGHTMEDS_INSTANCES, resolveKnightmedsInstanceId()),
        snapshot => observer.next(snapshot.get('color') === 'black' ? 'black' : 'maroon'),
        error => observer.error(error)
      )
    );
  }

  /** Get container models indexed by container id */
  getAllContainers(): Observable<Container[]> {
    const db = getFirestore();
    return new Observable<Container[]>(observer =>
      onSnapshot(
        collection(db, COLLECTION_KNIGHTMEDS_INSTANCES, resolveKnightmedsInstanceId(), COLLECTION_KNIGHTMEDS_CONTAINERS),
        snapshot => {
          const containers = snapshot.docs.map(d =>
            new Container(d.id, d.get('medicationId'), d.get('quantity')));
          containers.sort((a, b) => a.id - b.id);
          observer.next(containers);
        },
        error => observer.error(error)
      )
    );
  }

  /** Get a specific container from Firestore */
  getContainerById(containerId: ContainerId): Observable<Container> {
    const db = getFirestore();
    return new Observable<Container>(observer =>
      onSnapshot(
        doc(db, COLLECTION_KNIGHTMEDS_INSTANCES, resolveKnightmedsInstanceId(), COLLECTION_KNIGHTMEDS_CONTAINERS, containerId.toString()),
        snapshot => observer.next(new Container(snapshot.id, snapshot.get('medicationId'), snapshot.get('quantity'))),
        error => observer.error(error)
      )
    );
  }

  /**
   * Gets a container with the specified medication and at least the specified quantity.
   * Returns undefined if no container can be found.
   */
  getContainerByMedication(medicationId: string, quantity: number): Observable<Container | undefined> {
    return this.getAllContainers().pipe(
      map(containers =>
        containers.find(c =>
          c.medicationId?.toLowerCase() === medicationId.toLowerCase()
          && (!c.quantity || c.quantity >= quantity)
        )
      )
    );
  }

  /**
   * Checks if the medication for each provider order is available.
   * Returns an array of the ids of provider orders that have medication in the Knightmeds.
   */
  inStockProviderOrdersIds(providerOrders: ProviderOrder[]): Observable<string[]> {
    return this.getAllContainers().pipe(
      map(containers =>
        providerOrders
          .filter(order =>
            containers.find(c =>
              c.medicationId?.toLowerCase() === order.medId.toLowerCase()
              && (c.quantity == undefined || c.quantity >= order.quantity)
            )
          )
          .map(order => order.id)
      )
    );
  }

  /** Opens the specified container by sending a pulse to its relay */
  async openContainer(containerId: ContainerId): Promise<void> {
    await this.sendRelay(containerId, 'on');
    await new Promise(resolve => setTimeout(resolve, RELAY_PULSE_MS));
    await this.sendRelay(containerId, 'off');
    this.openContainerIds.add(containerId);
    this.notifyListeners();
  }

  /** Closes the specified container by de-energising its relay */
  async closeContainer(containerId: ContainerId): Promise<void> {
    await this.sendRelay(containerId, 'off');
    this.openContainerIds.delete(containerId);
    this.notifyListeners();
  }

  /** Registers a callback that fires whenever the any-containers-open state changes */
  addAnyContainersOpenListener(listener: (anyOpen: boolean) => void): void {
    this.anyContainersOpenListeners.push(listener);
  }

  /**
   * Records the medication id as stored in the container.
   * An undefined id indicates the container is empty.
   */
  async setMedicationId(containerId: ContainerId, medicationId: string | undefined): Promise<void> {
    const db = getFirestore();
    // setDoc(..., { merge: true }) rather than updateDoc(): a brand-new
    // instance has no pre-existing container documents, and updateDoc()
    // fails ("No document to update") the first time a fresh cabinet's
    // drawer is written to. merge:true creates the document on first write
    // and behaves the same as updateDoc() for an existing one.
    await setDoc(doc(db, COLLECTION_KNIGHTMEDS_INSTANCES, resolveKnightmedsInstanceId(), COLLECTION_KNIGHTMEDS_CONTAINERS, containerId.toString()), { medicationId: medicationId ?? null }, { merge: true });
  }

  /**
   * Records the quantity of medications in the container.
   * An undefined value indicates the quantity isn't specified.
   */
  async setQuantity(containerId: ContainerId, quantity: number | undefined): Promise<void> {
    const db = getFirestore();
    await setDoc(doc(db, COLLECTION_KNIGHTMEDS_INSTANCES, resolveKnightmedsInstanceId(), COLLECTION_KNIGHTMEDS_CONTAINERS, containerId.toString()), { quantity: quantity ?? null }, { merge: true });
  }

  private async sendRelay(containerId: ContainerId, action: 'on' | 'off'): Promise<void> {
    if (!this.device?.opened) return;
    const relayNum = getRelayNumber(containerId);
    const actionByte = action === 'on' ? 0x01 : 0x00;
    const startByte = 0xA0;
    const checksum = (startByte + relayNum + actionByte) & 0xFF;
    await this.device.sendReport(0x00, new Uint8Array([startByte, relayNum, actionByte, checksum]));
  }

  private notifyListeners(): void {
    const anyOpen = this.openContainerIds.size > 0;
    this.anyContainersOpenListeners.forEach(l => l(anyOpen));
  }
}
